import {
  forwardRef,
  FunctionComponent,
  PropsWithoutRef,
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo
} from 'react';

import {
  useConsolidatedRef,
  useUID,
  useActiveDescendant,
  useI18n,
  usePrevious,
  useDirection
} from '../../hooks';
import { ForwardProps } from '../../types';
import { cap } from '../../utils';
import VisuallyHiddenText from '../VisuallyHiddenText';

import FlyoutMenuList from './FlyoutMenuList';
import MenuContext from './Menu.context';
import { StyledMenu, StyledMenuListWrapper } from './Menu.styles';
import { MenuContextProps, MenuProps } from './Menu.types';
import MenuList from './MenuList';

const Menu: FunctionComponent<MenuProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<MenuProps>, ref: MenuProps['ref']) => {
    const uid = useUID();
    const {
      id = uid,
      items = [],
      mode = 'action',
      accent,
      scrollAt = 7,
      emptyText,
      onItemClick,
      onItemActive,
      onItemExpand,
      loadMore,
      onItemCollapse,
      loading = false,
      currentItemId,
      header,
      footer,
      variant = 'drill-down',
      focusControlEl,
      focusElOnClose = true,
      arrowNavigationUnsupported,
      'aria-describedby': ariaDescribedBy,
      ...restProps
    } = props;

    const t = useI18n();

    const { end: endDirection, start: startDirection } = useDirection();
    const radioName = useUID();
    const menuRef = useConsolidatedRef(ref);
    const previousItemCount = useRef(0);
    const [activeDescendantUpdateId, setActiveDescendantUpdateId] = useState(0);
    const [focusDescendant, setFocusDescendant] = useState<HTMLElement | null>(null);
    const [flyOutActiveIdStack, setFlyoutActiveIdStack] = useState([id]);
    const [focusReturnEl, setFocusReturnEl] = useState<HTMLElement | null>();
    const [preventInitialScroll, setPreventInitialScroll] = useState(false);
    const activeFlyoutSelector = useMemo(
      () =>
        `fieldset[data-flyout-menu-id="${flyOutActiveIdStack[flyOutActiveIdStack.length - 1]}"]`,
      [flyOutActiveIdStack]
    );
    const focusControl = useMemo(() => {
      return focusControlEl || menuRef.current;
    }, [focusControlEl, menuRef.current]);

    const pushFlyoutId = useCallback(
      (flyoutId: string) => {
        setFlyoutActiveIdStack([...flyOutActiveIdStack, flyoutId]);
      },
      [flyOutActiveIdStack]
    );

    const getScopedItemId = useCallback(
      (itemId: string) => {
        return `${id}-item-${itemId}`;
      },
      [id]
    );

    const getUnscopedItemId = useCallback(
      (itemId: string) => {
        return itemId.split(`${id}-item-`)[1];
      },
      [id]
    );

    const updateActiveDescendants: MenuContextProps['updateActiveDescendants'] = useCallback(
      ({ preventScroll = false } = {}) => {
        setPreventInitialScroll(preventScroll);
        setActiveDescendantUpdateId(Math.random());
      },
      []
    );

    const uadConfig = useMemo(
      () => ({
        focusEl: focusControl,
        scope: menuRef.current,
        scopeSelector:
          variant === 'drill-down'
            ? ('fieldset[aria-hidden="false"]' as keyof HTMLElementTagNameMap)
            : activeFlyoutSelector,
        selector: '[role="menuitem"], legend button' as keyof HTMLElementTagNameMap,
        focusDescendantEl: focusDescendant,
        clearFocusDescendant: () => {
          setFocusDescendant(null);
          if (focusElOnClose) focusControl?.focus();
        },
        focusReturnEl,
        clearFocusReturn: () => {
          setFocusReturnEl(null);
        },
        currentDescendantId: currentItemId ? getScopedItemId(currentItemId) : undefined,
        preventInitialScroll,
        clearPreventScroll: () => {
          setPreventInitialScroll(false);
        }
      }),
      [
        focusControl,
        menuRef.current,
        activeFlyoutSelector,
        focusReturnEl,
        focusDescendant,
        currentItemId,
        preventInitialScroll
      ]
    );

    const { activeDescendant, descendants } = useActiveDescendant(uadConfig, [
      activeDescendantUpdateId
    ]);

    const previousActiveDescendant = usePrevious(activeDescendant);

    // ## Bind Menu-specific navigation keyDown.
    useEffect(() => {
      const expandOrCollapse: (action?: 'collapse' | 'expand' | null) => void = (action = null) => {
        if (activeDescendant) {
          // expand
          if (activeDescendant.dataset.expand === 'true' && action !== 'collapse') {
            (activeDescendant!.querySelector('button') as HTMLElement)?.click();

            return;
          }

          // collapse
          if (activeDescendant.dataset.collapse === 'true' && action !== 'expand') {
            activeDescendant.click();
          }
        }
      };

      const additionalKeydown = (e: KeyboardEvent) => {
        switch (e.key) {
          case `Arrow${cap(endDirection)}`: {
            if (arrowNavigationUnsupported) break;
            expandOrCollapse('expand');
            break;
          }
          case `Arrow${cap(startDirection)}`: {
            if (arrowNavigationUnsupported) break;
            expandOrCollapse('collapse');
            break;
          }
          case 'Escape': {
            if (variant === 'flyout' && flyOutActiveIdStack.length > 1) {
              e.preventDefault();
              e.stopPropagation();

              const newStack = [...flyOutActiveIdStack];
              newStack.pop();
              setFlyoutActiveIdStack(newStack);
            }
            break;
          }
          default:
            break;
        }

        if (arrowNavigationUnsupported && (e.key === ' ' || e.key === 'Spacebar') && e.shiftKey) {
          e.preventDefault();
          expandOrCollapse();
        }
      };

      if (activeDescendant) onItemActive?.(getUnscopedItemId(activeDescendant.id));

      focusControl?.addEventListener('keydown', additionalKeydown);

      return () => focusControl?.removeEventListener('keydown', additionalKeydown);
    }, [focusControl, activeDescendant]);

    // ## Update useActiveDescendant on change of items, not selection of items.
    useEffect(() => {
      // Next tick for DOM updates.
      setTimeout(() => {
        if (items.length === previousItemCount.current) {
          const previousDescendantIds = descendants?.map(node => node.id);
          const newDescendants = uadConfig.scope
            ?.querySelector(uadConfig.scopeSelector)
            ?.querySelectorAll(uadConfig.selector);

          if (!newDescendants) {
            updateActiveDescendants();
            return;
          }

          const scopedDescendants = Array.from(newDescendants);

          if (scopedDescendants?.length !== previousDescendantIds?.length) {
            updateActiveDescendants();
            return;
          }

          const itemsUnchanged = scopedDescendants.every(
            (node, index) => node.id === previousDescendantIds[index]
          );

          if (!itemsUnchanged) {
            updateActiveDescendants();
          }

          return;
        }

        previousItemCount.current = items.length;
        updateActiveDescendants();
      }, 0);
    }, [items]);

    // ## Bump scroll & set return element when descendant focused in loading state.
    useEffect(() => {
      const activeDescendantChanged = previousActiveDescendant?.id !== activeDescendant?.id;
      if (!activeDescendantChanged) return;

      if (loading && descendants && descendants.length && activeDescendant) {
        const descendantList = [...descendants];
        const lastItem = descendantList.pop();
        const lastId = lastItem?.id || undefined;

        if (lastId === activeDescendant.id) {
          const scrollRegion = uadConfig.scope
            ?.querySelector(uadConfig.scopeSelector)
            ?.querySelector('ul');

          if (scrollRegion) {
            scrollRegion.scrollTop = scrollRegion.scrollHeight - scrollRegion.offsetHeight;
          }

          if (focusReturnEl?.id !== activeDescendant.id) {
            // Set focus return on 'nextTick' and avoid looping calls with immediate siblings.
            setTimeout(() => {
              setFocusReturnEl(lastItem);
            }, 0);
          }
        } else if (focusReturnEl?.id !== activeDescendant.id) {
          // Set focus return on 'nextTick' and avoid looping calls with immediate siblings.
          setTimeout(() => {
            setFocusReturnEl(activeDescendant);
          }, 0);
        }
      }
    }, [
      loading,
      descendants,
      previousActiveDescendant,
      activeDescendant,
      uadConfig,
      focusReturnEl
    ]);

    const contextValue = useMemo(() => {
      return {
        componentId: id,
        mode,
        arrowNavigationUnsupported,
        onItemClick,
        onItemActive,
        onItemExpand,
        onItemCollapse,
        accent,
        scrollAt,
        emptyText,
        radioName,
        loadMore,
        loading,
        variant,
        focusControl,
        updateActiveDescendants,
        setFocusReturnEl,
        setFocusDescendant,
        getScopedItemId,
        pushFlyoutId,
        flyOutActiveIdStack
      };
    }, [
      id,
      mode,
      arrowNavigationUnsupported,
      onItemClick,
      onItemActive,
      onItemExpand,
      onItemCollapse,
      accent,
      scrollAt,
      emptyText,
      radioName,
      loadMore,
      loading,
      variant,
      focusControl,
      updateActiveDescendants,
      setFocusReturnEl,
      setFocusDescendant,
      getScopedItemId,
      pushFlyoutId,
      flyOutActiveIdStack
    ]);

    return (
      <StyledMenu
        id={id}
        role='menu'
        aria-describedby={!focusControlEl ? `${id}-menuDescription` : undefined}
        {...restProps}
        ref={menuRef}
      >
        {!focusControlEl && (
          <VisuallyHiddenText id={`${id}-menuDescription`}>
            {(`${t('menu_selection_instructions')} ` && ariaDescribedBy) || ''}
          </VisuallyHiddenText>
        )}

        {header && <header>{header}</header>}

        <StyledMenuListWrapper>
          <MenuContext.Provider value={contextValue}>
            {variant === 'drill-down' ? (
              <MenuList items={items} />
            ) : (
              <FlyoutMenuList items={items} />
            )}
          </MenuContext.Provider>
        </StyledMenuListWrapper>
        {footer && <footer>{footer}</footer>}
      </StyledMenu>
    );
  }
);

export default Menu;
